/**
 * A day at the front desk.
 *
 * This is where most of a clinic group's revenue actually comes from — paid at
 * the counter, never becoming an invoice. Two things are load-bearing and both
 * are visible on this screen.
 *
 * **The day has to add up before it can post.** What was collected and what was
 * earned are two counts of the same thing, and a difference means somebody
 * miscounted. The screen shows the difference and refuses to post it, because
 * plugging it would put the miscount in the books.
 *
 * **Card and UPI go to transit, not to the bank.** The money arrives days later
 * and net of a fee, so saying it is in the bank today would be saying something
 * untrue about the balance.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'

interface Takings {
  id: string
  reference: string
  location_id: string
  location_name: string
  takings_on: string
  status: string
  cash_paise: number
  card_paise: number
  upi_paise: number
  exempt_paise: number
  taxable_paise: number
  cgst_paise: number
  sgst_paise: number
  collected_paise: number
  earned_paise: number
  difference_paise: number
  till_bank_account_id: string | null
  journal_entry_id: string | null
}

interface Location {
  id: string
  code: string
  name: string
  kind: string
}

interface BankAccount {
  id: string
  name: string
  kind: string
}

const today = () => new Date().toISOString().slice(0, 10)

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

export default function CounterTakings() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [drafting, setDrafting] = useState(false)
  const [location, setLocation] = useState('')
  const [till, setTill] = useState('')
  const [day, setDay] = useState(today())
  const [cash, setCash] = useState('')
  const [card, setCard] = useState('')
  const [upi, setUpi] = useState('')
  const [exempt, setExempt] = useState('')
  const [taxable, setTaxable] = useState('')
  const [rate, setRate] = useState('0')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['takings'] })
  }

  const days = useQuery({
    queryKey: ['takings'],
    queryFn: () => api.get<Takings[]>('/receivables/takings'),
  })
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
    enabled: can('ar.takings.create'),
  })
  const banks = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/banking/accounts'),
    enabled: can('ar.takings.create'),
  })

  const record = useMutation({
    mutationFn: (body: unknown) => api.post<Takings>('/receivables/takings', body),
    onSuccess: () => {
      refresh()
      setDrafting(false)
      setCash('')
      setCard('')
      setUpi('')
      setExempt('')
      setTaxable('')
    },
  })
  const post = useMutation({
    mutationFn: (id: string) => api.post<Takings>(`/receivables/takings/${id}/post`),
    onSuccess: refresh,
  })

  if (days.isLoading) return <Loading what="Loading the counter" />
  const rows = days.data ?? []
  const unposted = rows.filter((d) => d.status === 'draft')

  const collected = toPaise(cash) + toPaise(card) + toPaise(upi)
  const tax = Math.floor((toPaise(taxable) * Number(rate || '0')) / 10000)
  const earned = toPaise(exempt) + toPaise(taxable) + tax
  const difference = collected - earned

  return (
    <>
      <ErrorNote error={days.error ?? record.error ?? post.error} />

      {unposted.length > 0 && (
        <Note kind="warn">
          {unposted.length === 1
            ? 'One day is recorded and not yet in the books.'
            : `${unposted.length} days are recorded and not yet in the books.`}{' '}
          Revenue the clinic earned that the accounts do not know about.
        </Note>
      )}

      {can('ar.takings.create') && (
        <Card
          title="Record a day"
          actions={
            <button className="small" onClick={() => setDrafting(!drafting)}>
              {drafting ? 'Cancel' : 'New day'}
            </button>
          }
        >
          {!drafting ? (
            <p className="faint">
              One sheet per clinic per day. A second would double-count the day's
              income, and "which one is right" has no answer.
            </p>
          ) : (
            <>
              <div className="row">
                <Field label="Clinic">
                  <select value={location} onChange={(e) => setLocation(e.target.value)}>
                    <option value="">Choose…</option>
                    {(locations.data ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Day">
                  <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
                </Field>
                <Field label="Till" caption="where the cash went">
                  <select value={till} onChange={(e) => setTill(e.target.value)}>
                    <option value="">Choose…</option>
                    {(banks.data ?? [])
                      .filter((b) => b.kind === 'cash')
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                  </select>
                </Field>
              </div>

              <div className="row">
                <Field label="Cash">
                  <input className="num" value={cash} onChange={(e) => setCash(e.target.value)} />
                </Field>
                <Field label="Card" caption="to transit, not the bank">
                  <input className="num" value={card} onChange={(e) => setCard(e.target.value)} />
                </Field>
                <Field label="UPI" caption="to transit, not the bank">
                  <input className="num" value={upi} onChange={(e) => setUpi(e.target.value)} />
                </Field>
              </div>

              <div className="row">
                <Field label="Exempt revenue" caption="healthcare by a clinical establishment">
                  <input
                    className="num"
                    value={exempt}
                    onChange={(e) => setExempt(e.target.value)}
                  />
                </Field>
                <Field label="Taxable revenue" caption="pharmacy, most diagnostics">
                  <input
                    className="num"
                    value={taxable}
                    onChange={(e) => setTaxable(e.target.value)}
                  />
                </Field>
                <Field label="GST rate">
                  <select value={rate} onChange={(e) => setRate(e.target.value)}>
                    <option value="0">None</option>
                    <option value="500">5%</option>
                    <option value="1200">12%</option>
                    <option value="1800">18%</option>
                  </select>
                </Field>
              </div>

              <Note kind={difference === 0 ? 'ok' : 'warn'}>
                {formatInr(collected)} collected, {formatInr(earned)} accounted for
                {difference === 0
                  ? ' — the day adds up.'
                  : `, a difference of ${formatInr(difference)}. The day will not post until this is nil.`}
                {tax > 0 && ` Tax of ${formatInr(tax)} is computed, not typed in.`}
              </Note>

              <button
                disabled={!location || collected === 0}
                onClick={() =>
                  record.mutate({
                    location_id: location,
                    takings_on: day,
                    till_bank_account_id: till || null,
                    cash_paise: toPaise(cash),
                    card_paise: toPaise(card),
                    upi_paise: toPaise(upi),
                    exempt_paise: toPaise(exempt),
                    taxable_paise: toPaise(taxable),
                    gst_rate_bp: Number(rate || '0'),
                  })
                }
              >
                Save the day
              </button>
            </>
          )}
        </Card>
      )}

      <Card title="Days at the counter" hint={`${rows.length}`} tight>
        {rows.length === 0 ? (
          <div className="empty">No days recorded yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Clinic</th>
                <th>Day</th>
                <th>Status</th>
                <th className="num">Cash</th>
                <th className="num">Card</th>
                <th className="num">UPI</th>
                <th className="num">Earned</th>
                <th className="num">Difference</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.reference}</td>
                  <td>{row.location_name}</td>
                  <td className="muted date">{row.takings_on}</td>
                  <td>
                    <Pill value={row.status} />
                  </td>
                  <td className="num muted">{formatInr(row.cash_paise)}</td>
                  <td className="num muted">{formatInr(row.card_paise)}</td>
                  <td className="num muted">{formatInr(row.upi_paise)}</td>
                  <td className="num">{formatInr(row.earned_paise)}</td>
                  <td className="num">
                    {row.difference_paise === 0 ? (
                      <span className="faint">nil</span>
                    ) : (
                      <strong>{formatInr(row.difference_paise)}</strong>
                    )}
                  </td>
                  <td>
                    {row.status === 'draft' && can('ar.takings.post') && (
                      <button
                        className="small"
                        disabled={row.difference_paise !== 0}
                        title={
                          row.difference_paise !== 0
                            ? 'The day has to add up before it can be posted.'
                            : undefined
                        }
                        onClick={() => post.mutate(row.id)}
                      >
                        Post
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}
